// contexts/SubscriptionContext.tsx
import PricingModal from '@/pages/my-profile/components/PricingModal'
import { useLocalStorage } from '@/hooks/common'
import { useSubscription as usePaymentsSubscription } from '@/hooks/common/useSubscription'
import { FeatureKey, FEATURES_MAP, SubscriptionPlan } from '@/utils/subscriptions'
import React, { createContext, useContext, useState, useCallback } from 'react'
import toast from 'react-hot-toast'

interface SubscriptionContextValue {
  showPaywall: (feature?: FeatureKey) => void
  hidePaywall: () => void
  requirePro: (feature: FeatureKey, action: () => void) => void
  isFeatureAvailable: (feature: FeatureKey) => boolean
}

const SubscriptionContext = createContext<SubscriptionContextValue | null>(null)

export const SubscriptionProvider = ({ children }: { children: React.ReactNode }) => {
  const [visible, setVisible] = useState(false)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [ewUser] = useLocalStorage('ewUser', null) as [any, unknown]

  const { isPro, isEnterprise } = usePaymentsSubscription(ewUser?.userID)
  const activePlan = isEnterprise
    ? SubscriptionPlan.ENTERPRISE
    : isPro
      ? SubscriptionPlan.PRO
      : SubscriptionPlan.FREE

  const showPaywall = useCallback((feature?: FeatureKey) => {
    if (feature) {
      const { toastMessage } = FEATURES_MAP[feature]
      if (toastMessage.length > 0)
        setTimeout(() => {
          toast.error(toastMessage)
        }, 500)
    }

    setVisible(true)
  }, [])

  const hidePaywall = useCallback(() => {
    setVisible(false)
  }, [])

  const isFeatureAvailable = useCallback(
    (feature: FeatureKey) => {
      const f = FEATURES_MAP[feature]
      if (!f) return false
      if (f.free) return true

      return (
        (activePlan == SubscriptionPlan.PRO && f.pro) ||
        (activePlan == SubscriptionPlan.ENTERPRISE && f.enterprise) ||
        false
      )
    },
    [activePlan],
  )

  // Gate an action — runs it if pro, shows modal if not
  const requirePro = useCallback(
    (feature: FeatureKey, action: () => void) => {
      if (isFeatureAvailable(feature)) {
        action()
      } else {
        showPaywall(feature)
      }
    },
    [activePlan, showPaywall],
  )

  return (
    <SubscriptionContext.Provider
      value={{ showPaywall, hidePaywall, requirePro, isFeatureAvailable }}
    >
      {children}
      <PricingModal open={visible} onClose={hidePaywall} userId={ewUser?.userID} userEmail={ewUser?.userName} />
    </SubscriptionContext.Provider>
  )
}

export const useSubscription = () => {
  const ctx = useContext(SubscriptionContext)
  if (!ctx) throw new Error('useSubscription must be used within SubscriptionProvider')
  return ctx
}
